import * as yup from "yup";
import { Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "../../../components/ui/button";
import Common from "../../../helper/common";
import {
  FormikInput,
  FormikSearchSelect,
  FormikCheckbox,
} from "../../../components/formik-components";
import { respEnum, IntMax, IntMinLevel } from "../../../constants/appConstant";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const FormikInitialValues = {
  name: "",
  amenityid: null,
  defaultcharges: "",
  isactive: true,
};

const formikValidation = yup.object({
  name: yup
    .string()
    .trim()
    .min(2)
    .max(50)
    .required("Facility Name is required"),
  amenityid: yup
    .number()
    .nullable()
    .transform((value, originalValue) => {
      // Convert string numbers to actual numbers, keep null as null
      if (
        originalValue === null ||
        originalValue === undefined ||
        originalValue === ""
      ) {
        return null;
      }
      return Number(originalValue);
    })
    .required("Amenity Type is required"),
  defaultcharges: yup
    .number()
    .min(0)
    .max(IntMax)
    .required("Default Charges is required"),
  isactive: yup.boolean(),
});

const AddClubAmenity = ({ id, onClose, open, setOpen }) => {
  const formikRef = useRef();
  const dialogRef = useRef();
  const [amenityList, setAmenityList] = useState([]);
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState(FormikInitialValues);

  useEffect(() => {
    if (open) {
      loadAmenities();
    }
  }, [open]);

  // Reset initial values when dialog closes
  useEffect(() => {
    if (!open) {
      setInitialValues(FormikInitialValues);
    }
  }, [open]);

  // Track state changes for debugging if needed

  useEffect(() => {
    if (id && amenityList.length > 0) {
      loadFacilityDetails();
    }
  }, [id, amenityList]);

  const loadAmenities = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetMasterAmenities",
        {
          isactive: true,
        }
      );
      let masterData = response?.data ?? [];
      // Ensure all amenity IDs are numbers
      masterData = masterData.map((a) => ({ ...a, id: Number(a.id) }));

      // Always include the select option for consistency, but handle it in validation
      const amenities = [
        { id: null, name: "Select Amenity Type" },
        ...masterData,
      ];
      setAmenityList(amenities);

      if (!id) {
        setInitialValues(FormikInitialValues);
      }
    } catch (error) {
      console.error("Error loading amenities:", error);
    }
  };

  const loadFacilityDetails = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        `GetClubAmenityDetails?id=${id}`
      );
      if (response.status === respEnum.Success && response.data) {
        const amenityIdValue = Number(response.data.amenityid);

        // Find the matching amenity in the list to ensure it exists
        const matchingAmenity = amenityList.find(
          (a) => Number(a.id) === amenityIdValue
        );

        const newInitialValues = {
          name: response.data.name || "",
          amenityid: matchingAmenity ? amenityIdValue : null,
          defaultcharges: response.data.defaultcharges || 0,
          isactive: response.data.isactive ?? true,
        };

        setInitialValues(newInitialValues);

        // Force formik to update with new values if form is already rendered
        if (formikRef.current) {
          formikRef.current.setValues(newInitialValues);
        }

        // Successfully loaded and set facility details for editing
      }
    } catch (error) {
      console.error("Error loading facility details:", error);
    }
  };

  const onSubmitData = async (d, action) => {
    let data = {
      ...d,
      id,
    };
    let response = await Common.ApiService.getInstance().request(
      "AddUpdateClubAmenity",
      data,
      "POST"
    );
    action.setSubmitting(false);
    if (response.status === respEnum.Success) {
      onClose(response.data);
      setOpen(false);
      if (!onClose) {
        navigate("/app/ClubAmenities", { replace: true });
      }
    }
  };

  return (
    <div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            size="sm"
            variant="primary"
            className="flex items-center gap-2"
          >
            <Icon icon="heroicons:plus-20-solid" className="h-4 w-4" />
            Add
          </Button>
        </DialogTrigger>
        <DialogContent ref={dialogRef}>
          <DialogHeader>
            <DialogTitle className="text-base font-medium ">
              {!id ? "Add" : "Update"} Facility
            </DialogTitle>
          </DialogHeader>

          <div className="text-sm text-default-500  space-y-2">
            <Formik
              innerRef={formikRef}
              onSubmit={onSubmitData}
              initialValues={initialValues}
              enableReinitialize={true}
              validationSchema={formikValidation}
              validateOnChange={false}
              validateOnBlur={false}
            >
              {(formikProps) => (
                <div className="md:grid md:grid-cols-1 gap-6 mt-6 space-y-6 md:space-y-0">
                  <div className="col-span-1">
                    <div className="md:grid md:grid-cols-1  gap-6 mt-6 space-y-6 md:space-y-0">
                      <div className="flex flex-col gap-2">
                        <FormikInput
                          formikProps={formikProps}
                          name={"name"}
                          label={"Facility Name"}
                          placeholder={"Enter Facility Name"}
                          maxLength="50"
                        />
                      </div>

                      <div className="flex flex-col gap-3">
                        <FormikSearchSelect
                          formikProps={formikProps}
                          name={"amenityid"}
                          placeholder={"Select Amenity Type"}
                          label={"Amenity Type"}
                          data={amenityList}
                          valueKey="id"
                          labelKey="name"
                          required={true}
                        />
                      </div>

                      <div className="flex flex-col gap-3">
                        <FormikInput
                          formikProps={formikProps}
                          name={"defaultcharges"}
                          placeholder={"Default Charges"}
                          label={"Default Charges"}
                          type={"number"}
                          max={IntMax}
                          min={0}
                          onChange={(value) => {
                            if (value < 0 && value != "") {
                              formikProps.setFieldValue("defaultcharges", 0);
                            } else if (value >= IntMax) {
                              toast.error(
                                `Default charges cannot be greater than ${IntMax}`
                              );
                              formikProps.setFieldValue("defaultcharges", "");
                            } else {
                              formikProps.setFieldValue(
                                "defaultcharges",
                                value
                              );
                            }
                          }}
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <FormikCheckbox
                          formikProps={formikProps}
                          name={"isactive"}
                          label={"Is Active"}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Formik>
          </div>
          <DialogFooter className="mt-8 gap-2">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Close
              </Button>
            </DialogClose>

            {formikRef.current && (
              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  formikRef.current?.handleSubmit();
                }}
              >
                {formikRef.current.isSubmitting
                  ? "Saving..."
                  : !id
                  ? "Add"
                  : "Update"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddClubAmenity;
